import { IconName } from '@aidkitorg/component-library/dist/types/src/Icon';
import { NavigationNode } from '@aidkitorg/types/lib/translation/permissions';

export type NavItem = Omit<NavigationNode, 'href'> & {
  iconName?: IconName;
  href?: string;
  items?: Array<NavigationNode & { iconName?: IconName; href: string }>
  isExternal?: boolean
};

export const getNavItems = (L: any, userTags?: string[], userEmail?: string): NavItem[] => [
  {
    name: L.admin_panel.nav.home,
    href: '/admin-panel/home',
    iconName: 'HomeIcon'
  },
  {
    name: L.admin_panel.nav.applicants,
    iconName: 'UserCircleIcon',
    items: [
      {
        name: L.admin_panel.nav.all_applicants,
        href: '/admin-panel/applicants/all',
      },
      {
        name: L.admin_panel.nav.applicant_groups,
        href: '/admin-panel/applicants/applicant-groups',
      },
      {
        name: L.admin_panel.nav.onsite_intake,
        href: '/admin-panel/applicants/onsite-intake',
      },
    ]
  },
  {
    name: L.admin_panel.nav.dashboards,
    href: '/admin-panel/dashboards',
    iconName: 'PresentationChartBarIcon'
  },
  {
    name: L.admin_panel.nav.reports,
    href: '/admin-panel/reports',
    iconName: 'TableCellsIcon'
  },
  {
    name: L.admin_panel.nav.reviews,
    iconName: 'InboxStackIcon',
    items: [
      {
        name: L.admin_panel.nav.my_reviews,
        href: '/admin-panel/reviews/my-reviews',
        iconName: 'ClipboardDocumentCheckIcon'
      },
      {
        name: L.admin_panel.nav.review_queues,
        href: '/admin-panel/reviews/queues',
        iconName: 'QueueListIcon'
      }
    ]
  },
  {
    name: L.admin_panel.nav.accounting,
    iconName: 'CurrencyDollarIcon',
    items: [
      {
        name: L.admin_panel.nav.payments,
        href: '/admin-panel/payments',
        iconName: 'CurrencyDollarIcon'
      },
      {
        name: L.admin_panel.nav.balances,
        href: '/admin-panel/balances',
        iconName: 'WalletIcon'
      },
      // HARDCODED: Only show transactions to admins with @aidkit.cloud emails
      // When the User Management stuff comes out, this will be handled in a nicer way.
      ...(userTags?.includes('admin') && userEmail?.endsWith('@aidkit.cloud') ? [{
        name: L.admin_panel.nav.transactions,
        href: '/admin-panel/transactions',
        iconName: 'CurrencyDollarIcon' as IconName
      }] : []),
      ...(userTags?.includes('admin') && userEmail?.endsWith('@aidkit.cloud') ? [{
        name: 'Invoices',
        href: '/admin-panel/invoices',
        iconName: 'DocumentTextIcon' as IconName
      }] : []),
    ]
  },
  {
    name: L.admin_panel.nav.notifications,
    href: '/admin-panel/notifications',
    iconName: 'BellIcon'
  },
  {
    name: L.admin_panel.nav.user_management,
    iconName: 'UsersIcon',
    items: [
      {
        name: L.admin_panel.nav.users,
        href: '/admin-panel/user-management/users',
      },
      {
        name: L.admin_panel.user_groups.title,
        href: '/admin-panel/user-management/user-groups',
      },
      {
        name: L.admin_panel.nav.organizations,
        href: '/admin-panel/user-management/organizations',
      }
    ]
  },
  {
    name: L.admin_panel.nav.settings,
    iconName: 'CogIcon',
    items: [
      {
        name: L.admin_panel.settings.manage_fields,
        href: '/admin-panel/settings/manage-fields',
      }
    ]
  },
  {
    name: L.admin_panel.nav.audit_logs,
    href: '/admin-panel/audit-logs',
    iconName: 'DocumentTextIcon',
  },
  {
    name: L.admin_panel.nav.support,
    href: '/admin-panel/support',
    iconName: 'ChatBubbleLeftRightIcon',
  },
  {
    name: L.admin_panel.nav.documentation,
    href: 'https://community.aidkit.cloud/',
    iconName: 'BookOpenIcon',
    isExternal: true
  },
];

// Helper function to generate parent routes that need redirects
export const getParentRedirectRoutes = (navItems: NavItem[]): Array<{ from: string; to: string }> => {
  const redirects: Array<{ from: string; to: string }> = [];

  navItems.forEach(item => {
    // If item has subitems but no href, we need to create a redirect
    if (item.items && item.items.length > 0 && !item.href) {
      // Derive the parent path from the first child's href
      const firstChildHref = item.items[0].href;
      const parentPath = firstChildHref.substring(0, firstChildHref.lastIndexOf('/'));

      if (parentPath && firstChildHref) {
        redirects.push({
          from: parentPath,
          to: firstChildHref
        });
      }
    }
  });

  return redirects;
};
